import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { usePlaylist, usePlaylistProgress } from "../../../contexts/PlaylistContext";
import { trackClick } from "../../_helpers";
import useWindowSize from "../../../hooks/useWindowSize";

const COMPACT_TAP_SX = { padding: 0, flexShrink: 0, minWidth: { xs: 44, md: 0 }, minHeight: { xs: 44, md: 0 } };

const MusicPlayer = ({ artist, audioUrl, song, artwork, sx, variant = "auto" }: {
  artist: string;
  audioUrl: string;
  song: string;
  artwork: string;
  sx?: object;
  variant?: "auto" | "full" | "compact" | "minimized";
}) => {
  const {
    currentTrack,
    isPlaying,
    playlist,
    togglePlayPause,
    playNext,
    playPrevious,
    seek,
    addToPlaylist,
    togglePanel,
    togglePlayerVisibility,
  } = usePlaylist();
  const { currentTime, duration } = usePlaylistProgress();

  // Check if this track is in the playlist
  const isInPlaylist = playlist.some((track) => track.audioUrl === audioUrl);

  const handleAddToPlaylist = () => {
    trackClick("player-add-to-playlist", { label: "Add track to playlist", section: "music-player", artist, track: song });
    if (audioUrl && artist && song) {
      addToPlaylist({
        id: `${artist}-${song}`,
        audioUrl,
        title: song,
        artist,
        artwork,
      });
    }
  };

  const handlePlayPause = () => {
    trackClick(isPlaying ? "player-pause" : "player-play", {
      label: "Music player play toggle",
      section: "music-player",
      artist: currentTrack?.artist || artist,
      track: currentTrack?.title || song,
      trackId: currentTrack?.id,
    });
    // If no track is playing and we have a URL, add to playlist and play
    if (!currentTrack && audioUrl) {
      handleAddToPlaylist();
    } else {
      togglePlayPause();
    }
  };

  const windowSize = useWindowSize();
  const COMPACT_BREAKPOINT = 480;

  // Use current track info if available, otherwise use props
  const displayArtist = currentTrack?.artist || artist;
  const displaySong = currentTrack?.title || song;
  const hasAudio = currentTrack?.audioUrl || audioUrl;

  // Determine actual variant based on state and width
  const getActualVariant = () => {
    if (variant !== "auto") return variant;
    if (!currentTrack) return "minimized";
    if (windowSize.width > 0 && windowSize.width < COMPACT_BREAKPOINT) return "compact";
    return "full";
  };
  const actualVariant = getActualVariant();

  // Minimized state - no track playing
  if (actualVariant === "minimized") {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "95vw", md: "100%", lg: "50rem" },
          margin: "0 auto",
          padding: { xs: "0.75rem 1.5rem", md: "0.6rem 1.25rem" },
          borderRadius: { xs: "1.5rem", md: "2rem" },
          border: "2px solid var(--logoColor2)",
          backgroundColor: "var(--color1)",
          display: "flex",
          flexDirection: "row",
          gap: { xs: "2rem", md: "0.75rem" },
          justifyContent: "center",
          alignItems: "center",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          ...sx,
        }}
      >
        <IconButton
          onClick={handlePlayPause}
          disabled={!hasAudio}
          size="small"
          sx={{ padding: '0' }}
          aria-label="Play track"
        >
          <PlayArrowIcon sx={{ fontSize: { xs: "4.5rem", md: "1.75rem" }, color: "var(--textColor)" }} />
        </IconButton>

        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "2.5rem", md: "0.9rem" },
            fontWeight: 600,
          }}
        >
          Ready to play
        </Typography>

        <Tooltip title="Open playlist">
          <span>
            <IconButton onClick={() => {
              trackClick("player-open-playlist", { label: "Open playlist panel", section: "music-player" });
              togglePanel();
            }} size="small" aria-label="Open playlist" sx={{ color: "var(--textColor)", padding: '0' }}>
              <PlaylistPlayIcon aria-hidden="true" sx={{ fontSize: { xs: "4.5rem", md: "1.75rem" } }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  // Compact state - for narrow widths, shows play/pause, progress bar, and playlist
  if (actualVariant === "compact") {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "95vw", md: "100%" },
          margin: "0 auto",
          padding: { xs: "1.25rem 1.5rem", md: "0.75rem 1rem" },
          borderRadius: { xs: "1.5rem", md: "1.25rem" },
          border: "2px solid var(--logoColor2)",
          backgroundColor: "var(--color1)",
          display: "flex",
          flexDirection: "column-reverse",
          gap: { xs: "0", md: "0.5rem" },
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          ...sx,
        }}
      >
        {/* Track info row */}
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "1.75rem", md: "0.85rem" },
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "center",
            p: { xs: '0.5rem', md: '0.25rem' },
            backgroundColor: 'var(--sidebarSurfaceColor, var(--logoColor))',
            color: 'var(--sidebarSurfaceTextColor, var(--textColor))',
            borderRadius: '1rem',
            filter: 'brightness(0.9)'
          }}
        >
          {displayArtist} - {displaySong}
        </Typography>

        {/* Controls row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "1rem", md: "0.5rem" } }}>
          <IconButton
            onClick={handlePlayPause}
            disabled={!hasAudio}
            size="small"
            sx={COMPACT_TAP_SX}
            aria-label={isPlaying ? "Pause track" : "Play track"}
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: { xs: "3.5rem", md: "2rem" }, color: "var(--textColor)" }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: { xs: "3.5rem", md: "2rem" }, color: "var(--textColor)" }} />
            )}
          </IconButton>

          {/* Progress slider */}
          <Slider
            disabled={!duration || !hasAudio}
            size="small"
            value={currentTime}
            max={duration || 100}
            onChange={(_, newValue) => seek(Array.isArray(newValue) ? newValue[0] : newValue)}
            onChangeCommitted={() => trackClick("player-seek", { label: "Seek track", section: "music-player", artist: displayArtist, track: displaySong })}
            aria-label="Track progress"
            sx={{
              flex: 1,
              color: "var(--logoColor2)",
              height: { xs: "0.5rem", md: "0.25rem" },
              ".MuiSlider-thumb": {
                width: { xs: "1.25rem", md: "0.75rem" },
                height: { xs: "1.25rem", md: "0.75rem" },
                backgroundColor: "var(--logoColor2)",
              },
              ".MuiSlider-track": { backgroundColor: "var(--logoColor2)" },
              ".MuiSlider-rail": { opacity: 0.3, backgroundColor: "var(--logoColor)" },
            }}
          />

          {/* Time display */}
          <Typography
            sx={{
              color: "var(--textColor)",
              fontFamily: "Poppins",
              fontSize: { xs: "1.5rem", md: "0.75rem" },
              minWidth: { xs: "5.5rem", md: "3.5rem" },
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, "0")} / {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, "0")}
          </Typography>

          <IconButton onClick={() => {
              trackClick("player-open-playlist", { label: "Open playlist panel", section: "music-player" });
              togglePanel();
            }} size="small" aria-label="Open playlist" sx={{ ...COMPACT_TAP_SX, color: "var(--logoColor2)" }}>
            <PlaylistPlayIcon aria-hidden="true" sx={{ fontSize: { xs: "3.5rem", md: "1.5rem" } }} />
          </IconButton>

          <IconButton onClick={() => {
              trackClick("player-minimize", { label: "Minimize player", section: "music-player" });
              togglePlayerVisibility();
            }} size="small" aria-label="Minimize player" sx={{ ...COMPACT_TAP_SX, color: "var(--logoColor)" }}>
            <CloseIcon aria-hidden="true" sx={{ fontSize: { xs: "3.5rem", md: "1.5rem" } }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // Full state - all controls visible
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "95vw", md: "100%", lg: "50rem" },
        margin: "0 auto",
        padding: { xs: "1.75rem 2rem", md: "1.25rem 2rem" },
        borderRadius: { xs: "2rem", md: "3.125rem" },
        border: "2px solid var(--logoColor2)",
        backgroundColor: "var(--color1)",
        display: "flex",
        flexDirection: "row",
        gap: { xs: "1.5rem", md: "1.25rem" },
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        ...sx,
      }}
    >
      {/* Play controls */}
      <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
        <Tooltip title="Previous">
          <span>
            <IconButton onClick={() => { trackClick("player-previous", { label: "Previous track", section: "music-player" }); playPrevious(); }} disabled={!currentTrack} size="small" aria-label="Previous track" sx={{ padding: '0' }}>
              <SkipPreviousIcon
                aria-hidden="true"
                sx={{
                  fontSize: { xs: "3rem", md: "1.5rem" },
                  color: currentTrack ? "var(--textColor)" : "var(--logoColor)",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        <IconButton
          onClick={handlePlayPause}
          disabled={!hasAudio}
          size="small"
          sx={{ padding: '0' }}
          aria-label={isPlaying ? "Pause track" : "Play track"}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: { xs: "4rem", md: "2rem" }, color: "var(--textColor)" }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: { xs: "4rem", md: "2rem" }, color: "var(--textColor)" }} />
          )}
        </IconButton>

        <Tooltip title="Next">
          <span>
            <IconButton onClick={() => { trackClick("player-next", { label: "Next track", section: "music-player" }); playNext(); }} disabled={!currentTrack} size="small" aria-label="Next track" sx={{ padding: '0' }}>
              <SkipNextIcon
                aria-hidden="true"
                sx={{
                  fontSize: { xs: "3rem", md: "1.5rem" },
                  color: currentTrack ? "var(--textColor)" : "var(--logoColor)",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Track name - desktop only */}
      <Typography
        sx={{
          color: "var(--textColor)",
          fontFamily: "Poppins",
          fontSize: { xs: "1rem", md: "1rem" },
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: { xs: "none", md: "block" },
          maxWidth: "12rem",
          flexShrink: 0,
        }}
      >
        {displayArtist} - {displaySong}
      </Typography>

      {/* Time and seek bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.75rem", md: "0.75rem" }, flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: "var(--textColor)",
            fontFamily: "Poppins",
            fontSize: { xs: "2rem", md: "0.85rem" },
            minWidth: { xs: "2.75rem", md: "2.25rem" },
            flexShrink: 0,
          }}
        >
          {Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, "0")}
        </Typography>

        <Slider
          disabled={!duration || !hasAudio}
          size="small"
          value={currentTime}
          max={duration || 100}
          onChange={(_, newValue) => seek(Array.isArray(newValue) ? newValue[0] : newValue)}
          aria-label="Track progress"
          aria-valuetext={`${Math.floor(currentTime / 60)}:${String(Math.floor(currentTime % 60)).padStart(2, "0")} of ${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, "0")}`}
          sx={{
            flex: 1,
            color: "var(--logoColor2)",
            height: { xs: "0.5rem", md: "0.25rem" },
            ".MuiSlider-thumb": {
              width: { xs: "1.5rem", md: "0.75rem" },
              height: { xs: "1.5rem", md: "0.75rem" },
              backgroundColor: "var(--logoColor2)",
              "&:hover": {
                boxShadow: "0 0 0 0.5rem rgba(255, 255, 255, 0.16)",
              },
            },
            ".MuiSlider-track": {
              backgroundColor: "var(--logoColor2)",
            },
            ".MuiSlider-rail": {
              opacity: 0.3,
              backgroundColor: "var(--logoColor)",
            },
          }}
        />

        <Typography
          sx={{
            color: "var(--textColor)",
            fontFamily: "Poppins",
            fontSize: { xs: "2rem", md: "0.85rem" },
            minWidth: { xs: "2.75rem", md: "2.25rem" },
            flexShrink: 0,
          }}
        >
          {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, "0")}
        </Typography>
      </Box>

      {/* Add to playlist and toggle panel buttons */}
      <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
        <Tooltip title={isInPlaylist ? "In playlist" : "Add to playlist"}>
          <span>
            <IconButton
              onClick={handleAddToPlaylist}
              disabled={!audioUrl || isInPlaylist}
              size="small"
              aria-label={isInPlaylist ? "Already in playlist" : "Add to playlist"}
              sx={{
                color: isInPlaylist ? "var(--logoColor)" : "var(--logoColor2)",
                padding: '0',
              }}
            >
              <AddIcon aria-hidden="true" sx={{ fontSize: { xs: "4.5rem", md: "1.5rem" } }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Open playlist">
          <span>
            <IconButton onClick={() => {
              trackClick("player-open-playlist", { label: "Open playlist panel", section: "music-player" });
              togglePanel();
            }} size="small" aria-label="Open playlist" sx={{ color: "var(--logoColor2)", padding: '0' }}>
              <PlaylistPlayIcon aria-hidden="true" sx={{ fontSize: { xs: "4.5rem", md: "1.5rem" } }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Minimize player">
          <span>
            <IconButton onClick={() => {
              trackClick("player-minimize", { label: "Minimize player", section: "music-player" });
              togglePlayerVisibility();
            }} size="small" aria-label="Minimize player" sx={{ color: "var(--logoColor)", padding: '0' }}>
              <CloseIcon aria-hidden="true" sx={{ fontSize: { xs: "4.5rem", md: "1.5rem" } }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default MusicPlayer;
